import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import {
  mediaQueryLarge,
  isMobileBreakpoint,
  getIOSVersion,
  onAnimationEnd,
  isPointWithinElement,
  isLightBackground,
} from '@theme/utilities';
import { acquireScrollLock, releaseScrollLock } from '@theme/scroll-lock';
import { loadDeferredStylesheet } from '@theme/deferred-scripts';

/** @type {Record<string, string>} */
const DEFERRED_STYLES = (() => {
  try {
    const el = document.getElementById('ea-deferred-styles');
    return el?.textContent ? JSON.parse(el.textContent) : {};
  } catch {
    return {};
  }
})();

/** @returns {Promise<void>} */
function ensureQuickAddStyles() {
  return loadDeferredStylesheet(DEFERRED_STYLES.quickAddModal);
}

/** Monotonic generation — stale fetch/morph completions are ignored after a newer open. */
let quickAddOpenGeneration = 0;
/** Section Rendering API section for Quick Add (complete modal markup, no full PDP). */
const QUICK_ADD_SECTION_ID = 'quick-add-rendering';

/** @type {number} */
const HTML_PREFETCH_DEBOUNCE_MS = 280;

/** @type {Set<string>} */
const preloadedFeaturedImageUrls = new Set();

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  /** @type {ReturnType<typeof setTimeout> | null} */
  #htmlPrefetchTimer = null;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cancelHtmlPrefetch();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Hover: preload hero image immediately; debounce HTML prefetch for cache.
   */
  preloadForHover() {
    void ensureQuickAddStyles();
    this.#preloadFeaturedImageFromCard();
    this.#scheduleHtmlPrefetch();
  }

  /** Cancels a scheduled HTML prefetch (e.g. pointer left the card). */
  cancelHtmlPrefetch() {
    this.#cancelHtmlPrefetch();
  }

  #preloadFeaturedImageFromCard() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const fromCard = productCard?.getAttribute('data-featured-media-url')?.trim();
    if (fromCard) QuickAddComponent.#preloadFeaturedImageUrl(fromCard);
  }

  #scheduleHtmlPrefetch() {
    this.#cancelHtmlPrefetch();
    this.#htmlPrefetchTimer = setTimeout(() => {
      this.#htmlPrefetchTimer = null;
      void this.#prefetchHtmlCache();
    }, HTML_PREFETCH_DEBOUNCE_MS);
  }

  #cancelHtmlPrefetch() {
    if (this.#htmlPrefetchTimer != null) {
      clearTimeout(this.#htmlPrefetchTimer);
      this.#htmlPrefetchTimer = null;
    }
  }

  async #prefetchHtmlCache() {
    const url = this.productPageUrl;
    if (!url || this.#cachedContent.has(url)) return;

    try {
      const document = await this.fetchProductPage(url);
      this.#storeGridInCache(document, url);
    } catch {
      // Prefetch is best-effort
    }
  }

  /**
   * @param {Document | null} document
   * @param {string} productPageUrl
   */
  #storeGridInCache(document, productPageUrl) {
    if (!document || !QuickAddComponent.#hasCompleteQuickAddGrid(document)) return;

    const gridElement = document.querySelector('[data-product-grid-content]');
    if (!gridElement) return;

    this.#cachedContent.set(productPageUrl, /** @type {Element} */ (gridElement.cloneNode(true)));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    await ensureQuickAddStyles();

    const generation = ++quickAddOpenGeneration;
    const currentUrl = this.productPageUrl;
    const isCached = this.#cachedContent.has(currentUrl);

    if (!isCached) {
      dialogComponent.showSkeleton();
    }

    this.#stayVisibleUntilDialogCloses(dialogComponent);
    dialogComponent.showDialog();

    try {
      let productGrid = this.#cachedContent.get(currentUrl);

      if (!productGrid) {
        const html = await this.fetchProductPage(currentUrl);
        if (generation !== quickAddOpenGeneration) return;
        this.#storeGridInCache(html, currentUrl);
        productGrid = this.#cachedContent.get(currentUrl);
      }

      if (productGrid && generation === quickAddOpenGeneration) {
        const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
        await this.updateQuickAddModal(freshContent);

        if (generation !== quickAddOpenGeneration) return;

        if (!isCached) {
          await QuickAddDialog.waitForPrimaryModalImage();
        }
      }
    } finally {
      if (generation === quickAddOpenGeneration) {
        dialogComponent.hideSkeleton();
      }
    }
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches quick-add-rendering section HTML; falls back to full PDP if incomplete.
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const sectionDocument = await this.#fetchSectionDocument(productPageUrl, QUICK_ADD_SECTION_ID);

      if (QuickAddComponent.#hasCompleteQuickAddGrid(sectionDocument)) {
        this.#preloadFeaturedImageFromDocument(sectionDocument);
        return sectionDocument;
      }

      const fullPageDocument = await this.#fetchFullPageDocument(productPageUrl);
      this.#preloadFeaturedImageFromDocument(fullPageDocument);
      return fullPageDocument;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      }
      throw error;
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * @param {string} productPageUrl
   * @param {string} sectionId
   * @returns {Promise<Document | null>}
   */
  async #fetchSectionDocument(productPageUrl, sectionId) {
    const url = new URL(productPageUrl);
    url.searchParams.set('section_id', sectionId);

    const response = await fetch(url.toString(), {
      signal: this.#abortController?.signal,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch section ${sectionId}: HTTP error ${response.status}`);
    }

    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }

  /**
   * @param {string} productPageUrl
   * @returns {Promise<Document | null>}
   */
  async #fetchFullPageDocument(productPageUrl) {
    const response = await fetch(productPageUrl, {
      signal: this.#abortController?.signal,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
    }

    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }

  /**
   * Section Rendering API can return the grid shell without theme blocks (no price / variant picker).
   * @param {Document | null} document
   */
  static #hasCompleteQuickAddGrid(document) {
    const grid = document?.querySelector('[data-product-grid-content]');
    if (!grid) return false;

    const picker = grid.querySelector('variant-picker');
    const hasPicker =
      picker &&
      (picker.querySelector('fieldset, select') ||
        Boolean(picker.querySelector('script[type="application/json"]')?.textContent?.trim()));

    return Boolean(hasPicker && grid.querySelector('product-price'));
  }

  /**
   * @param {Document | null} document
   */
  #preloadFeaturedImageFromDocument(document) {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const fromCard = productCard?.getAttribute('data-featured-media-url')?.trim();

    const grid = document?.querySelector('[data-product-grid-content]');
    const galleryImg = grid?.querySelector(
      '.product-information__media img[src*="cdn"], .product-information__media img[srcset*="cdn"]'
    );
    const fromGallery =
      galleryImg instanceof HTMLImageElement
        ? galleryImg.currentSrc || galleryImg.src || galleryImg.getAttribute('srcset')?.split(/\s+/)[0]
        : null;

    const url = fromCard || fromGallery;
    if (url) QuickAddComponent.#preloadFeaturedImageUrl(url);
  }

  /**
   * @param {string} url
   */
  static #preloadFeaturedImageUrl(url) {
    const normalized = url.trim();
    if (!normalized || preloadedFeaturedImageUrls.has(normalized)) return;

    preloadedFeaturedImageUrls.add(normalized);

    const separator = normalized.includes('?') ? '&' : '?';
    const src = /[?&]width=/i.test(normalized)
      ? normalized
      : `${normalized}${separator}width=1200`;

    const img = new Image();
    img.decoding = 'async';
    img.src = src;
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#initializeModalVariantPicker(modalContent);
    this.#syncVariantSelection(modalContent);
  }

  /**
   * Layout-only init after morph (not a second picker morph).
   * @param {Element} modalContent
   */
  #initializeModalVariantPicker(modalContent) {
    const picker = modalContent.querySelector('variant-picker');
    if (!picker) return;

    queueMicrotask(() => {
      if (typeof picker.updateVariantPickerCss === 'function') {
        picker.updateVariantPickerCss();
      }
      if (typeof picker.sortSizeOptions === 'function') {
        picker.sortSizeOptions();
      }
      if (typeof picker.ensureSelectableSelections === 'function') {
        picker.ensureSelectableSelections();
      }
    });
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (
        input instanceof HTMLInputElement &&
        input.dataset.variantId === selectedVariantId &&
        input.dataset.optionAvailable !== 'false' &&
        !input.disabled &&
        !input.checked
      ) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  /** @type {number} */
  #previousScrollY = 0;

  /** @type {boolean} */
  #isClosing = false;

  /** @type {boolean} */
  #dismissListenersAttached = false;

  /** @type {boolean} */
  static #dismissListenersAdded = false;

  /** @type {string} */
  static #skeletonMarkup = `
    <div class="quick-add-modal__skeleton" data-quick-add-skeleton aria-hidden="true">
      <div class="quick-add-modal__skeleton-gallery">
        <div class="quick-add-modal__skeleton-image"></div>
      </div>
      <div class="quick-add-modal__skeleton-details">
        <div class="quick-add-modal__skeleton-header">
          <div class="quick-add-modal__skeleton-line quick-add-modal__skeleton-line--title"></div>
          <div class="quick-add-modal__skeleton-line quick-add-modal__skeleton-line--price"></div>
        </div>
        <div class="quick-add-modal__skeleton-field">
          <div class="quick-add-modal__skeleton-line quick-add-modal__skeleton-line--label"></div>
          <div class="quick-add-modal__skeleton-color-cards">
            <div class="quick-add-modal__skeleton-color-card">
              <div class="quick-add-modal__skeleton-color-media"></div>
              <div class="quick-add-modal__skeleton-line quick-add-modal__skeleton-line--card-name"></div>
            </div>
            <div class="quick-add-modal__skeleton-color-card">
              <div class="quick-add-modal__skeleton-color-media"></div>
              <div class="quick-add-modal__skeleton-line quick-add-modal__skeleton-line--card-name"></div>
            </div>
            <div class="quick-add-modal__skeleton-color-card">
              <div class="quick-add-modal__skeleton-color-media"></div>
              <div class="quick-add-modal__skeleton-line quick-add-modal__skeleton-line--card-name"></div>
            </div>
          </div>
        </div>
        <div class="quick-add-modal__skeleton-field">
          <div class="quick-add-modal__skeleton-line quick-add-modal__skeleton-line--label"></div>
          <div class="quick-add-modal__skeleton-sizes">
            <div class="quick-add-modal__skeleton-size"></div>
            <div class="quick-add-modal__skeleton-size"></div>
            <div class="quick-add-modal__skeleton-size"></div>
            <div class="quick-add-modal__skeleton-size"></div>
          </div>
        </div>
        <div class="quick-add-modal__skeleton-line quick-add-modal__skeleton-line--button"></div>
      </div>
    </div>
  `;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.#detachDismissListeners();
    this.#clearDismissCursor();
  }

  showSkeleton() {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    modalContent.innerHTML = QuickAddDialog.#skeletonMarkup;
    modalContent.classList.add('quick-add-modal__content--loading');
  }

  hideSkeleton() {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    modalContent.classList.remove('quick-add-modal__content--loading');
    modalContent.querySelector('[data-quick-add-skeleton]')?.remove();
  }

  /**
   * Keeps the skeleton visible until the modal gallery's primary image has loaded.
   * @param {number} [timeoutMs]
   */
  static waitForPrimaryModalImage(timeoutMs = 8000) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return Promise.resolve();

    const img =
      modalContent.querySelector(
        '.product-information__media slideshow-slide:not([aria-hidden="true"]) img, .product-information__media slideshow-slide:first-child img'
      ) ?? modalContent.querySelector('.product-information__media img');

    if (!(img instanceof HTMLImageElement)) return Promise.resolve();

    if (img.complete && img.naturalWidth > 0) return Promise.resolve();

    return new Promise((resolve) => {
      const finish = () => {
        clearTimeout(timer);
        img.removeEventListener('load', finish);
        img.removeEventListener('error', finish);
        resolve();
      };

      const timer = setTimeout(finish, timeoutMs);
      img.addEventListener('load', finish, { once: true });
      img.addEventListener('error', finish, { once: true });
    });
  }

  showDialog() {
    const { dialog } = this.refs;
    if (dialog.open) return;

    this.#setDismissCursor();
    acquireScrollLock();

    requestAnimationFrame(() => {
      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());
      this.#attachDismissListeners();
    });
  }

  closeDialog = async () => {
    const { dialog } = this.refs;
    if (!dialog?.open || this.#isClosing) return;

    quickAddOpenGeneration++;
    this.#isClosing = true;
    this.#clearDismissCursor();
    this.#detachDismissListeners();

    dialog.classList.add('dialog-closing');
    dialog.style.animation = 'none';
    void dialog.offsetWidth;
    dialog.style.animation = '';

    await Promise.race([
      onAnimationEnd(dialog, undefined, { subtree: false }),
      new Promise((resolve) => setTimeout(resolve, 400)),
    ]);

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    releaseScrollLock();

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
    this.#isClosing = false;
  };

  /**
   * @returns {HTMLElement | null}
   */
  #getPanel() {
    const panel = this.refs.dialog?.querySelector('.quick-add-modal__content');
    return panel instanceof HTMLElement ? panel : null;
  }

  #attachDismissListeners() {
    if (this.#dismissListenersAttached) return;

    const { dialog } = this.refs;
    if (!dialog) return;

    dialog.addEventListener('click', this.#handleDialogClick);
    dialog.addEventListener('keydown', this.#handleDialogKeydown);
    dialog.addEventListener('cancel', this.#handleCancel);
    this.#enableDocumentDismissListeners();
    this.#dismissListenersAttached = true;
  }

  #detachDismissListeners() {
    if (!this.#dismissListenersAttached) return;

    const { dialog } = this.refs;
    dialog?.removeEventListener('click', this.#handleDialogClick);
    dialog?.removeEventListener('keydown', this.#handleDialogKeydown);
    dialog?.removeEventListener('cancel', this.#handleCancel);
    this.#disableDocumentDismissListeners();
    this.#dismissListenersAttached = false;
  }

  #enableDocumentDismissListeners() {
    if (QuickAddDialog.#dismissListenersAdded) return;
    document.addEventListener('pointerdown', QuickAddDialog.#onDocumentPointerdown, true);
    document.addEventListener('click', QuickAddDialog.#onDocumentClick, true);
    document.addEventListener('keydown', QuickAddDialog.#onDocumentKeydown, true);
    QuickAddDialog.#dismissListenersAdded = true;
  }

  #disableDocumentDismissListeners() {
    if (!QuickAddDialog.#dismissListenersAdded) return;
    document.removeEventListener('pointerdown', QuickAddDialog.#onDocumentPointerdown, true);
    document.removeEventListener('click', QuickAddDialog.#onDocumentClick, true);
    document.removeEventListener('keydown', QuickAddDialog.#onDocumentKeydown, true);
    QuickAddDialog.#dismissListenersAdded = false;
  }

  /**
   * @param {PointerEvent} event
   */
  static #onDocumentPointerdown = (event) => {
    QuickAddDialog.#dismissFromDocumentEvent(event);
  };

  /**
   * @param {MouseEvent | PointerEvent} event
   */
  static #dismissFromDocumentEvent(event) {
    const quickAddDialog = document.getElementById('quick-add-dialog');
    if (!(quickAddDialog instanceof QuickAddDialog)) return;

    const { dialog } = quickAddDialog.refs;
    if (!dialog?.open || quickAddDialog.#isClosing) return;

    if (!quickAddDialog.#shouldDismissFromClick(event)) return;

    if (event.cancelable) {
      event.preventDefault();
    }
    event.stopImmediatePropagation();
    void quickAddDialog.closeDialog();
  }

  /**
   * @param {MouseEvent | PointerEvent} event
   * @returns {boolean}
   */
  #shouldDismissFromClick(event) {
    const panel = this.#getPanel();
    if (!panel) return true;
    if (!(event.target instanceof Element)) return true;

    if (panel.contains(event.target) && event.target !== panel) {
      return false;
    }

    if (event.target instanceof HTMLDialogElement) {
      return !isPointWithinElement(event.clientX, event.clientY, panel);
    }

    return !panel.contains(event.target);
  }

  /**
   * @param {MouseEvent} event
   */
  #handleDialogClick = (event) => {
    if (!this.#shouldDismissFromClick(event)) return;
    event.preventDefault();
    void this.closeDialog();
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleDialogKeydown = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    void this.closeDialog();
  };

  /**
   * @param {Event} event
   */
  #handleCancel = (event) => {
    event.preventDefault();
    void this.closeDialog();
  };

  /**
   * Capture-phase: close on backdrop / outside the panel (right-panel pattern).
   * @param {MouseEvent} event
   */
  static #onDocumentClick = (event) => {
    QuickAddDialog.#dismissFromDocumentEvent(event);
  };

  /**
   * @param {KeyboardEvent} event
   */
  static #onDocumentKeydown = (event) => {
    if (event.key !== 'Escape') return;

    const quickAddDialog = document.getElementById('quick-add-dialog');
    if (!(quickAddDialog instanceof QuickAddDialog)) return;

    const { dialog } = quickAddDialog.refs;
    if (!dialog?.open || quickAddDialog.#isClosing) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    void quickAddDialog.closeDialog();
  };

  #setDismissCursor() {
    document.body.classList.add('quick-add-open');
    document.body.dataset.dismissCursor = QuickAddDialog.#resolveDismissCursorMode();
  }

  #clearDismissCursor() {
    document.body.classList.remove('quick-add-open');
    delete document.body.dataset.dismissCursor;
  }

  /**
   * @returns {'light' | 'dark'}
   */
  static #resolveDismissCursorMode() {
    const header = document.querySelector('header-component');
    const bg =
      header && getComputedStyle(header).getPropertyValue('--color-background').trim()
        ? getComputedStyle(header).getPropertyValue('--color-background').trim()
        : getComputedStyle(document.body).backgroundColor;

    return isLightBackground(bg) ? 'light' : 'dark';
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #handleVariantUpdate = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (anchorElement) {
      if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
      if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
    }

    const preview = event.detail.resource?.featured_media?.preview_image;
    if (preview?.src) {
      this.#updateModalPrimaryImage(preview.src, preview.width);
    }
  };

  /**
   * @param {string} src
   * @param {number} [width]
   */
  #updateModalPrimaryImage(src, width = 1200) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    const img =
      modalContent.querySelector(
        '.product-information__media slideshow-slide:not([aria-hidden="true"]) img, .product-information__media slideshow-slide:first-child img'
      ) ?? modalContent.querySelector('.product-information__media img');

    if (!(img instanceof HTMLImageElement)) return;

    const separator = src.includes('?') ? '&' : '?';
    img.src = `${src}${separator}width=${width}`;
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
  }

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
